
import React, { useState } from "react";
import { Book, Heart, Home, MessageSquare, Settings, ShoppingBag, Users, Image, BookOpen, PenTool, BriefcaseIcon, Store, FolderIcon, HandCoins } from "lucide-react";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const sidebarItems = [
  { icon: Home, label: "Home", path: "/" },
  { icon: FolderIcon, label: "Gerenciamento de Conteúdo", submenu: true, items: [
    { label: "Meus Pins", path: "/my-pins" },
    { label: "Meus Webtoons", path: "/my-webtoons" },
    { label: "Meus Webnovels", path: "/my-webnovels" },
  ]},
  { icon: Users, label: "Colabs", path: "/collabs" },
  { icon: Heart, label: "Commissions", path: "/commissions" },
  { icon: BriefcaseIcon, label: "Jobs", path: "/jobs" },
  { icon: ShoppingBag, label: "Shop", path: "/shop" },
  { icon: Store, label: "Minha Loja", path: "/my-store" },
  { icon: HandCoins, label: "Apoia-se", path: "/campaigns" },
  { icon: Settings, label: "Configurações", path: "/settings" },
];

export const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  return (
    <aside 
      className={cn(
        "fixed left-0 top-16 h-[calc(100vh-4rem)] bg-background/80 backdrop-blur-lg border-r border-border transition-all duration-300 z-40 hidden md:block",
        collapsed ? "w-0" : "w-16"
      )}
    >
      <motion.div
        initial={{ opacity: 0.6 }}
        whileHover={{ opacity: 1 }}
        className={cn(
          "absolute -right-5 top-4 bg-background rounded-lg border border-border shadow-lg z-50 p-1",
          collapsed && "-right-7"
        )}
      >
        <Button
          variant="ghost"
          size="icon"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="text-foreground/60 hover:text-foreground hover:bg-background/10 w-6 h-6 p-0"
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
              <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="2" fill="none" />
              <path d="M10 7L15 12L10 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="5" y1="3" x2="5" y2="21" stroke="currentColor" strokeWidth="2" />
            </svg>
          ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
              <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="2" fill="none" />
              <path d="M14 7L9 12L14 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </Button>
      </motion.div>
      
      <div className={cn(
        "flex flex-col items-center gap-6 py-6 transition-opacity duration-300",
        collapsed ? "opacity-0" : "opacity-100"
      )}>
        <TooltipProvider>
          {sidebarItems.map((item) => {
            if (item.submenu) {
              return (
                <Popover key={item.label} open={openSubmenu === item.label} onOpenChange={(open) => setOpenSubmenu(open ? item.label : null)}>
                  <Tooltip key={`tooltip-${item.label}`}>
                    <TooltipTrigger asChild>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className={cn(
                            "text-foreground/60 hover:text-foreground hover:bg-background/10",
                            openSubmenu === item.label && "bg-background/20 text-primary"
                          )}
                        >
                          <item.icon className="h-5 w-5" />
                        </Button>
                      </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{item.label}</p>
                    </TooltipContent>
                  </Tooltip>
                  <PopoverContent 
                    side="right" 
                    align="start" 
                    className="p-2 w-48 bg-background/95 backdrop-blur-lg border border-border rounded-lg shadow-lg"
                  >
                    <div className="text-sm font-medium py-1 px-2 text-muted-foreground">{item.label}</div>
                    <div className="flex flex-col gap-1 mt-1">
                      {item.items?.map((subitem) => (
                        <Link 
                          key={subitem.path} 
                          to={subitem.path}
                          className={cn(
                            "text-sm px-2 py-1.5 rounded-md hover:bg-muted transition-colors",
                            location.pathname === subitem.path && "bg-muted font-medium text-primary"
                          )}
                          onClick={() => setOpenSubmenu(null)}
                        >
                          {subitem.label}
                        </Link>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              );
            }
            
            return (
              <Tooltip key={`tooltip-${item.label}`}>
                <TooltipTrigger asChild>
                  <Link to={item.path}>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className={cn(
                        "text-foreground/60 hover:text-foreground hover:bg-background/10",
                        location.pathname === item.path && "bg-background/20 text-primary"
                      )}
                    >
                      <item.icon className="h-5 w-5" />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{item.label}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </TooltipProvider>
      </div>
    </aside>
  );
};
